import React, { useEffect, useState } from 'react';
import {
  Box, Typography, TextField, MenuItem, Button, useTheme, useMediaQuery,
  Autocomplete, Accordion, AccordionSummary, AccordionDetails, Slider, Avatar
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BusinessIcon from '@mui/icons-material/Business';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import WorkIcon from '@mui/icons-material/Work';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import SchoolIcon from '@mui/icons-material/School';
import PsychologyIcon from '@mui/icons-material/Psychology';
import BadgeIcon from '@mui/icons-material/Badge';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import PositionsSettings from './PositionsSettings';
import PersonalInfoFieldsSettings from './PersonalInfoFieldsSettings';
import RequiredDocsSettings from './RequiredDocsSettings';
import Supervisors from './Supervisors';
import BranchManagement from './BranchManagement';
import CustomLearningModulesManager from './CustomLearningModulesManager';
import { useTranslation } from '../i18n/i18n';
import API_ENDPOINTS from '../config/apiConfig';
import FixedSnackbar from './FixedSnackbar';

export default function AdminSettings() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));

  const initialCompany = {
    commercial_name: '',
    legal_name: '',
    rfc: '',
    fiscal_regime: ''
  };

  const initialAddress = {
    street: '',
    numExt: '',
    numInt: '',
    neigborhood: '',
    cp: '',
    city: '',
    state: '',
    country: ''
  };

  const [company, setCompany] = useState(initialCompany);
  const [address, setAddress] = useState(initialAddress);
  const [totalQuestions, setTotalQuestions] = useState(20);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const mexicoData = require('../data/states-cities/Mexico.json');
  const mexicoStates = React.useMemo(() => Object.keys(mexicoData || {}), [mexicoData]);
  const citiesForState = React.useMemo(() => (address.state && mexicoData[address.state]) || [], [address.state, mexicoData]);

  useEffect(() => {
    fetch(`${API_ENDPOINTS.BASEURL}/api/companypreferences/general`, { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        if (data) {
          if (data.commercial_name) setCompany(prev => ({ ...prev, commercial_name: data.commercial_name }));
          if (data.legal_name) setCompany(prev => ({ ...prev, legal_name: data.legal_name }));
          if (data.rfc) setCompany(prev => ({ ...prev, rfc: data.rfc }));
          if (data.fiscal_regime) setCompany(prev => ({ ...prev, fiscal_regime: data.fiscal_regime }));
          if (data.address) setAddress(prev => ({ ...prev, ...data.address }));
        }
      })
      .catch(() => {});

    fetch(`${API_ENDPOINTS.BASEURL}/api/companypreferences/psychometric`, { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        if (data && data.total_questions) setTotalQuestions(data.total_questions);
      })
      .catch(() => {});
  }, []);

  const handleCompanyChange = (field, value) => {
    setCompany(prev => ({ ...prev, [field]: value }));
  };

  const handleAddressChange = (field, value) => {
    setAddress(prev => ({ ...prev, [field]: value }));
  };

  const handleSavePsychometric = () => {
    fetch(`${API_ENDPOINTS.BASEURL}/api/companypreferences/psychometric`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ total_questions: totalQuestions })
    })
      .then(res => res.json())
      .then(data => {
        setSnackbar({ open: true, message: data.message || t('adminsettings_saved_success'), severity: 'success' });
      })
      .catch(() => setSnackbar({ open: true, message: t('adminsettings_saved_error'), severity: 'error' }));
  };

  const handleSave = () => {
    fetch(`${API_ENDPOINTS.BASEURL}/api/companypreferences/general`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ ...company, address })
    })
      .then(res => res.json())
      .then(data => {
        setSnackbar({ open: true, message: data.message || t('adminsettings_saved_success'), severity: 'success' });
      })
      .catch(() => setSnackbar({ open: true, message: t('adminsettings_saved_error'), severity: 'error' }));
  };

  const sections = [
    {
      key: 'company',
      title: t('company_profile'),
      icon: <BusinessIcon sx={{ fontSize: 18 }} />,
      content: (
        <Box>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'text.secondary' }}>
              {t('company_basic')}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField size="small" label={t('company_commercial_name')} value={company.commercial_name} onChange={e => handleCompanyChange('commercial_name', e.target.value)} />
              <TextField size="small" label={t('company_legal_name')} value={company.legal_name} onChange={e => handleCompanyChange('legal_name', e.target.value)} />
              <TextField size="small" label={t('company_rfc')} value={company.rfc} onChange={e => handleCompanyChange('rfc', e.target.value.toUpperCase())} />
              <TextField size="small" label={t('company_fiscal_regime')} value={company.fiscal_regime} onChange={e => handleCompanyChange('fiscal_regime', e.target.value)} />
            </Box>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'text.secondary' }}>
              {t('company_fiscal_address')}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField size="small" label={t('personaldocs_field_street')} value={address.street} onChange={e => handleAddressChange('street', e.target.value)} sx={{ gridColumn: { sm: 'span 2' } }} />
              <TextField size="small" label={t('personaldocs_field_numExt')} value={address.numExt} onChange={e => handleAddressChange('numExt', e.target.value)} />
              <TextField size="small" label={t('personaldocs_field_numInt')} value={address.numInt} onChange={e => handleAddressChange('numInt', e.target.value)} />
              <TextField size="small" label={t('personaldocs_field_neigborhood')} value={address.neigborhood} onChange={e => handleAddressChange('neigborhood', e.target.value)} sx={{ gridColumn: { sm: 'span 2' } }} />
              <TextField size="small" label={t('personaldocs_field_cp')} value={address.cp} onChange={e => handleAddressChange('cp', e.target.value)} />
              <TextField size="small" select label={t('personaldocs_field_country')} value={address.country} onChange={e => { handleAddressChange('country', e.target.value); handleAddressChange('state', ''); handleAddressChange('city', ''); }}>
                <MenuItem value="" disabled>{t('personaldocs_field_country_placeholder')}</MenuItem>
                <MenuItem value="Mexico">{t('country_mexico')}</MenuItem>
                <MenuItem value="United States of America">{t('country_usa')}</MenuItem>
              </TextField>
              {address.country === 'Mexico' ? (
                <>
                  <Autocomplete options={mexicoStates} value={address.state || null} onChange={(e, val) => { handleAddressChange('state', val || ''); handleAddressChange('city', ''); }} renderInput={(params) => <TextField {...params} label={t('personaldocs_field_state')} size="small" />} />
                  <Autocomplete options={citiesForState} value={address.city || null} onChange={(e, val) => handleAddressChange('city', val || '')} disabled={!address.state} renderInput={(params) => <TextField {...params} label={t('personaldocs_field_city')} size="small" />} />
                </>
              ) : (
                <>
                  <TextField size="small" label={t('personaldocs_field_state')} value={address.state} onChange={e => handleAddressChange('state', e.target.value)} />
                  <TextField size="small" label={t('personaldocs_field_city')} value={address.city} onChange={e => handleAddressChange('city', e.target.value)} />
                </>
              )}
            </Box>
          </Box>

          <Button variant="contained" onClick={handleSave} sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none', '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.12)' } }} fullWidth={isXs}>
            {t('save')}
          </Button>
        </Box>
      )
    },
    {
      key: 'branches',
      title: t('branch_management'),
      icon: <AccountTreeIcon sx={{ fontSize: 18 }} />,
      content: <BranchManagement />
    },
    {
      key: 'positions',
      title: t('positions_settings'),
      icon: <WorkIcon sx={{ fontSize: 18 }} />,
      subtitle: t('positions_settings_subtitle'),
      content: <PositionsSettings />
    },
    {
      key: 'supervisors',
      title: t('supervisors_panel'),
      icon: <GroupAddIcon sx={{ fontSize: 18 }} />,
      content: <Supervisors />
    },
    {
      key: 'learning',
      title: t('learning_modules_settings'),
      icon: <SchoolIcon sx={{ fontSize: 18 }} />,
      subtitle: t('learning_modules_settings_subtitle'),
      content: <CustomLearningModulesManager />
    },
    {
      key: 'psychometric',
      title: t('psychometric_test_settings'),
      icon: <PsychologyIcon sx={{ fontSize: 18 }} />,
      subtitle: t('psychometric_test_settings_subtitle'),
      content: (
        <Box sx={{ maxWidth: { xs: '100%', sm: 500 } }}>
          <Typography variant="subtitle2" gutterBottom>
            {t('total_psychometric_questions')}: <strong>{totalQuestions}</strong>
          </Typography>
          <Slider
            value={totalQuestions}
            onChange={(e, val) => setTotalQuestions(val)}
            min={20}
            max={200}
            step={20}
            marks={Array.from({ length: 10 }, (_, i) => ({ value: (i + 1) * 20, label: `${(i + 1) * 20}` }))}
            valueLabelDisplay="auto"
            sx={{ mt: 2, mb: 2 }}
          />
          <Button variant="contained" onClick={handleSavePsychometric} sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none', '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.12)' } }} fullWidth={isXs}>
            {t('save')}
          </Button>
        </Box>
      )
    },
    {
      key: 'personalinfo',
      title: t('personal_info_settings'),
      icon: <BadgeIcon sx={{ fontSize: 18 }} />,
      content: <PersonalInfoFieldsSettings />
    },
    {
      key: 'docs',
      title: t('docs_to_request_settings'),
      icon: <DriveFileRenameOutlineIcon sx={{ fontSize: 18 }} />,
      content: <RequiredDocsSettings />
    }
  ];

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, sm: 3, md: 4 }, py: { xs: 3, sm: 4 } }}>
      {/* Page Header */}
      <Box sx={{ mb: { xs: 3, sm: 4 } }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          {t('administration')}
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 640 }}>
          {t('admin_subtitle') || 'Configure your organization settings, branches, positions, and more.'}
        </Typography>
      </Box>

      {/* Sections */}
      {sections.map((section, idx) => (
        <Accordion
          key={section.key}
          defaultExpanded={false}
          sx={{
            mb: 2,
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
            '&::before': { display: 'none' },
            boxShadow: 'none',
            '&.Mui-expanded': { margin: 0, mb: 2 }
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              minHeight: 56,
              '& .MuiAccordionSummary-content': { my: 1.5 },
              '&.Mui-expanded': { minHeight: 56 }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', color: 'white' }}>
                {section.icon}
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {section.title}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 3 }}>
            {section.subtitle && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {section.subtitle}
              </Typography>
            )}
            {section.content}
          </AccordionDetails>
        </Accordion>
      ))}

      <FixedSnackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </Box>
  );
}
